import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Code2, Database, Server, Rocket, ShieldCheck, AlertCircle, Network } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";

// Build-time define (vite.config.ts); declared like the other public client surfaces.
declare const __API_URL__: string | undefined;

// Same resolution the other public client surfaces use.
const API_BASE =
  (typeof __API_URL__ !== "undefined" ? __API_URL__ : "") ||
  (typeof import.meta !== "undefined" ? (import.meta as any).env?.VITE_API_URL ?? "" : "") ||
  "http://localhost:3000";

const EXAMPLE_PROMPT_KEYS = ["lpp.prompt1", "lpp.prompt2", "lpp.prompt3"];

const SECURITY_KEYS: Record<string, string> = {
  standard: "lpp.secStandard",
  elevated:  "lpp.secElevated",
  critical:  "lpp.secCritical",
};

const LAYER_ICON: Record<string, typeof Code2> = {
  frontend: Code2,
  api:      Server,
  database: Database,
  auth:     ShieldCheck,
  deploy:   Rocket,
};

interface ArchitectureLayer { key: string; label: string; items: string[] }
interface ArchitecturePreview {
  prompt: string; appType: string; domain: string; domainKnown: boolean;
  confidence: number; security: "standard" | "elevated" | "critical";
  entities: string[]; workflows: string[]; roles: string[];
  layers: ArchitectureLayer[];
}


type Phase = "idle" | "loading" | "done" | "error";

interface LiveProductPreviewProps {
  /** Compact presentation for the public hero; behavior and API path stay identical. */
  compact?: boolean;
}

export function LiveProductPreview({ compact = false }: LiveProductPreviewProps) {
  const { t } = useI18n();
  const [prompt, setPrompt] = useState("");
  const [phase, setPhase] = useState<Phase>("idle");
  const [preview, setPreview] = useState<ArchitecturePreview | null>(null);
  const [visibleLayers, setVisibleLayers] = useState(0);
  const [placeholderIdx, setPlaceholderIdx] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Rotate placeholder while idle.
  useEffect(() => {
    if (phase !== "idle") return;
    const intervalId = setInterval(() => setPlaceholderIdx((p) => (p + 1) % EXAMPLE_PROMPT_KEYS.length), 4000);
    return () => clearInterval(intervalId);
  }, [phase]);

  // Reveal real layers one by one once the plan arrives.
  useEffect(() => {
    if (phase !== "done" || !preview) return;
    setVisibleLayers(0);
    let count = 0;
    const interval = setInterval(() => {
      count++;
      setVisibleLayers(count);
      if (count >= preview.layers.length) clearInterval(interval);
    }, 450);
    return () => clearInterval(interval);
  }, [phase, preview]);

  const handleGenerate = async () => {
    const text = prompt.trim() || t(EXAMPLE_PROMPT_KEYS[placeholderIdx]);
    if (!prompt.trim()) setPrompt(text);
    setPhase("loading");
    setPreview(null);
    setErrorMsg("");
    try {
      const res = await fetch(`${API_BASE}/api/public/architecture-preview`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ prompt: text }),
        signal:  AbortSignal.timeout(8_000),
      });
      const json = await res.json();
      if (!res.ok || !json?.success) {
        throw new Error(json?.error?.message ?? "Could not build a preview.");
      }
      setPreview(json.data.preview as ArchitecturePreview);
      setPhase("done");
    } catch (err: any) {
      setErrorMsg(err?.name === "TimeoutError" ? t("lpp.timeout") : (err?.message ?? t("lpp.previewError")));
      setPhase("error");
    }
  };

  const handleReset = () => {
    setPhase("idle");
    setPreview(null);
    setVisibleLayers(0);
    setPrompt("");
    setErrorMsg("");
  };

  return (
    <div className={compact ? "w-full" : "max-w-3xl mx-auto"}>
      <div className={"rounded-2xl border border-border/60 bg-card/60 backdrop-blur overflow-hidden " + (compact ? "shadow-[0_24px_80px_-36px_hsl(var(--primary)/0.55)]" : "")}>
        {/* Header bar.
            This was macOS traffic lights over the address "nuvra.ai/builder", with a "Live
            preview" pill on the right. Three problems in one strip: that domain is not Nuvra's
            (nuvrahq.com is), a drawing of a browser is the most generic thing a SaaS page can put
            on screen, and the panel does not render a live preview at all, it resolves an
            architecture. Replaced with a plain label for what this actually is. */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/40 bg-muted/20">
          <Network className="h-3.5 w-3.5 text-primary shrink-0" />
          <span className="text-xs font-medium tracking-wide text-muted-foreground">{t("lpp.panelLabel")}</span>
        </div>

        {/* Content */}
        <div className={compact ? "p-4 sm:p-5" : "p-5 sm:p-6"}>
          {phase === "idle" || phase === "error" ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Sparkles className="h-4 w-4 text-primary" />
                <span>{t("lpp.describe")}</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  ref={inputRef}
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  aria-label={t("lpp.describe")}
                  placeholder={t(EXAMPLE_PROMPT_KEYS[placeholderIdx])}
                  className="flex-1 min-w-0 h-11 rounded-xl border border-border bg-background px-4 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-ring"
                  onKeyDown={(e) => e.key === "Enter" && handleGenerate()}
                />
                <Button onClick={handleGenerate} className="h-11 px-5 w-full sm:w-auto shrink-0">
                  {t("lpp.generate")} <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
              {phase === "error" && (
                <div className="flex items-center gap-2 text-xs text-destructive">
                  <AlertCircle className="h-3.5 w-3.5" /> {errorMsg}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {EXAMPLE_PROMPT_KEYS.map((pk) => (
                  <button
                    key={pk}
                    onClick={() => { setPrompt(t(pk)); inputRef.current?.focus(); }}
                    className="w-full sm:w-auto text-xs px-3 py-1.5 rounded-full border border-border/50 text-muted-foreground hover:border-primary/40 hover:text-foreground transition-colors text-left sm:text-center"
                  >
                    {t(pk)}
                  </button>
                ))}
              </div>
            </div>
          ) : phase === "loading" ? (
            <div className="flex items-center gap-3 text-sm py-10 justify-center">
              <Sparkles className="h-4 w-4 text-primary animate-spin" />
              <span className="text-muted-foreground">{t("lpp.analyzing")}</span>
            </div>
          ) : preview ? (
            <div className="space-y-4">
              {/* Plan summary - real, derived from the prompt */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">{preview.appType}</span>
                <span className="text-[11px] px-2 py-0.5 rounded-full border border-border/60 text-muted-foreground">{preview.domain}</span>
                <span className="text-[11px] px-2 py-0.5 rounded-full border border-border/60 text-muted-foreground">{t("lpp.securityBadge", { level: t(SECURITY_KEYS[preview.security] ?? "lpp.secStandard") })}</span>
                {preview.domainKnown && (
                  <span className="text-[11px] px-2 py-0.5 rounded-full border border-border/60 text-muted-foreground">{t("lpp.confidenceMatch", { n: String(preview.confidence) })}</span>
                )}
              </div>
              <p className="text-sm text-muted-foreground italic">"{preview.prompt}"</p>

              <div className="space-y-3 mt-2">
                <AnimatePresence>
                  {preview.layers.slice(0, visibleLayers).map((layer) => {
                    const Icon = LAYER_ICON[layer.key] ?? Code2;
                    return (
                      <motion.div
                        key={layer.label}
                        initial={{ opacity: 0, y: 15, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 0.4 }}
                        className="rounded-xl border border-border/50 bg-muted/20 p-3"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <Icon className="h-4 w-4 text-primary" />
                          <span className="text-xs font-semibold text-foreground">{layer.label}</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {layer.items.map((f) => (
                            <span key={f} className="text-[11px] px-2 py-0.5 rounded bg-primary/10 text-primary font-mono">{f}</span>
                          ))}
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>

              {visibleLayers >= preview.layers.length && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="space-y-3 mt-4"
                >
                  {preview.workflows.length > 0 && (
                    <p className="text-[11px] text-muted-foreground">
                      {t("lpp.coreWorkflows")} {preview.workflows.map(w => w.replace(/_/g, " ")).join(" · ")}
                    </p>
                  )}
                  <div className="flex gap-3">
                    <Button asChild className="flex-1">
                      <Link to="/signup">{t("lpp.buildApp")} <ArrowRight className="ml-1 h-4 w-4" /></Link>
                    </Button>
                    <Button variant="outline" onClick={handleReset}>{t("lpp.tryAnother")}</Button>
                  </div>
                </motion.div>
              )}
            </div>
          ) : null}
        </div>
      </div>
      {!compact && (
        <p className="text-center text-[11px] text-muted-foreground/70 mt-3">
          {t("lpp.footer")}
        </p>
      )}
    </div>
  );
}
